<template>
  <div class="attack-dashboard-tables-wrapper is-fullwidth">
    <tabs-wrapper marker-style="top" :tabs="tabs">
      <template v-for="tab in tabs" :key="tab.title" #[tab.title]>
        <rbz-table
          v-show="tab.dataFilter(data)?.length > 0"
          :columns="tab.tableColumns"
          :row-classes="
            (item) => {
              return getBlockage(item) > 30 ? 'high-blockage' : '';
            }
          "
          :table-data="tab.dataFilter(data)"
          :default-sort-column-index="tab.defaultSortColumnIndex"
          default-sort-column-direction="desc"
          data-qa="dashboard-table"
          :number-of-results-in-column="tab.NumberOfResultsColumn"
          :show-top-navbar="false"
          rows-per-page="all"
          :show-buttons-column="false"
        >
          <template #countryFlagCell="cellProps">
            <span class="flag-cell-wrapper">
              <country-flag
                :country="getCountryCode(cellProps.col.displayFunction(cellProps.row))"
                v-b-tooltip="cellProps.col.displayFunction(cellProps.row)"
              />
            </span>
          </template>
        </rbz-table>
        <rbz-loader
          :loading="!!isTopxLoading"
          :data-exists="tab.dataFilter(data)?.length > 0"
          :no-data-message="$t('dashboard.noSearchResultsMessage')"
          class="table-loader"
          height="220px"
        />
      </template>
    </tabs-wrapper>
    <rbz-dropdown-menu
      dropdown-type="contextmenu"
      :context-menu-position="[contextMenuTop, contextMenuLeft]"
      ref="contextMenuRef"
    >
      <template #contextMenuContent>
        <menu-list-item
          :title="$t('dashboard.tables.contextMenu.copyValue')"
          classes="context-menu-item ignore-fix-focus"
          data-qa="copy-to-clipboard-button"
          @item-click="contextMenuCopyToClipboard()"
        />
        <layout-divider-line color="light-gray" />
        <menu-list-item
          :title="$t('dashboard.tables.contextMenu.showMatching')"
          classes="context-menu-item ignore-fix-focus"
          data-qa="add-to-filter-positive-button"
          @item-click="contextMenuAddToFilter(false)"
        />
        <layout-divider-line color="light-gray" />
        <menu-list-item
          :title="$t('dashboard.tables.contextMenu.hideMatching')"
          classes="context-menu-item ignore-fix-focus"
          data-qa="add-to-filter-negative-button"
          @item-click="contextMenuAddToFilter(true)"
        />
        <layout-divider-line color="light-gray" />
        <router-link :to="eventsLogPath" tabindex="-1" class="router-link">
          <menu-list-item
            :title="$t('dashboard.tables.contextMenu.eventsLogMatching')"
            @item-click="navigateToEventsLog(contextMenuFilter(false))"
            classes="context-menu-item ignore-fix-focus"
            data-qa="events-log-positive-link-button"
          />
        </router-link>
        <layout-divider-line color="light-gray" />
        <router-link :to="eventsLogPath" tabindex="-1" class="router-link">
          <menu-list-item
            :title="$t('dashboard.tables.contextMenu.eventsLogOther')"
            @item-click="navigateToEventsLog(contextMenuFilter(true))"
            classes="context-menu-item ignore-fix-focus"
            data-qa="events-log-negative-link-button"
          />
        </router-link>
      </template>
    </rbz-dropdown-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType } from 'vue';
import TabsWrapper from 'ui-component-library/rbz/components/TabsWrapper.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { Countries, DashboardTableEntry } from '@/types';
import Utils from 'ui-component-library/helpers/Utils';
import _ from 'lodash';
import RbzLoader from '@/components/RbzLoader.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import { mapStores } from 'pinia';
import { useDatasetsStore } from '@/store/DatasetsStore';
import { useBranchesStore } from '@/store/BranchesStore';
import RbzDropdownMenu from '@/components/RbzDropdownMenu.vue';
import { RouteParams } from 'vue-router';
import CountryFlag from 'ui-component-library/rbz/components/CountryFlag.vue';
import { RbzTableColumnOptions } from 'ui-component-library/rbz/types';
import MenuListItem from 'ui-component-library/rbz/components/MenuListItem.vue';

type DashboardTableTab = {
  title: string;
  icon: string;
  defaultSortColumnIndex: number;
  tableColumns: RbzTableColumnOptions[];
  dataFilter: (data: DashboardTableEntry[]) => DashboardTableEntry[];
  NumberOfResultsColumn: number;
};

type DataNamesMap = {
  [_key in DashboardTableEntry['label']]: {
    tabHeader: string;
    keyColumnHeader: string;
    filter: string;
  };
};

export default defineComponent({
  name: 'RbzDashboardTables',
  components: {
    MenuListItem,
    CountryFlag,
    LayoutDividerLine,
    RbzDropdownMenu,
    RbzLoader,
    RbzTable,
    TabsWrapper,
  },
  props: {
    data: Array as PropType<DashboardTableEntry[]>,
    isTopxLoading: Boolean,
  },
  emits: ['add-filter'],
  data() {
    const baseColumn: Partial<RbzTableColumnOptions> = {
      isSortable: true,
      isNumber: true,
      cellContentClasses: 'ellipsis',
      classes: 'width-100px',
    };
    const baseKeyColumn: Partial<RbzTableColumnOptions> = {
      id: 'key',
      fieldNames: ['key'],
      onContextMenu: (event, item) => {
        this.openContextMenu(event, item, 'key');
      },
      onClick: (event, item) => {
        this.openContextMenu(event, item, 'key');
      },
      displayFunction: (item) => {
        return this.displayFunctionEmptyToDash(item, 'key');
      },
      isSortable: true,
      cellContentClasses: 'ellipsis is-clickable',
    };
    const hitsColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'hits',
      title: this.$t('dashboard.tables.columns.hits'),
      displayFunction: (item) => {
        return Utils.amountSuffixFormatter(item['num_of_requests'], this.$t);
      },
      tooltipFunction: (item) => {
        return item['num_of_requests']?.toString() || '0';
      },
      tooltipFunctionAlwaysDisplayed: true,
      fieldNames: ['num_of_requests'],
      isSortable: true,
      isSortByOriginalValue: true,
      classes: 'width-75px',
    };
    const passedColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'passed',
      title: this.$t('dashboard.tables.columns.passed'),
      fieldNames: ['num_of_requests', 'num_of_blocked_requests', 'num_of_challenges'],
      displayFunction: (item) => {
        return (
          item['num_of_requests'] - item['num_of_blocked_requests'] - item['num_of_challenges']
        );
      },
      classes: 'width-90px passed border-left',
      cellContentClasses: 'passed',
    };
    const blockedColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'blocked',
      title: this.$t('dashboard.tables.columns.blocked'),
      fieldNames: ['num_of_blocked_requests'],
      classes: 'width-90px blocked',
      cellContentClasses: 'blocked',
    };
    const challengesColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'challenges',
      title: this.$t('dashboard.tables.columns.challenges'),
      fieldNames: ['num_of_challenges'],
      isSortable: true,
      classes: 'width-115px challenged border-right',
      cellContentClasses: 'challenged',
    };
    const humansColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'humans',
      title: this.$t('dashboard.tables.columns.humans'),
      fieldNames: ['num_of_human_requests'],
      isSortable: true,
      classes: 'width-95px humans',
      cellContentClasses: 'humans',
    };
    const botsColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'bots',
      title: this.$t('dashboard.tables.columns.bots'),
      fieldNames: ['num_of_bot_requests'],
      isSortable: true,
      classes: 'width-75px bots border-right',
      cellContentClasses: 'bots',
    };
    const blockageColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'blockage',
      title: this.$t('dashboard.tables.columns.blockage'),
      fieldNames: ['num_of_challenges', 'num_of_blocked_requests', 'num_of_requests'],
      isSortByOriginalValue: true,
      displayFunction: (item) => {
        return `${this.getBlockage(item).toFixed(2)}%`;
      },
      sortDisplayFunction: (item) => {
        return Math.round(this.getBlockage(item) * 100) / 100;
      },
      isSortable: true,
      isNumber: true,
    };
    const downColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'down',
      title: this.$t('dashboard.tables.columns.down'),
      fieldNames: ['sum_of_bytes_sent'],
      isSortByOriginalValue: true,
      displayFunction: (item) => {
        return Utils.amountSuffixFormatterBytes(item['sum_of_bytes_sent'], this.$t);
      },
      isSortable: true,
      classes: 'width-90px',
    };
    const upColumn: RbzTableColumnOptions = {
      ...baseColumn,
      id: 'up',
      title: this.$t('dashboard.tables.columns.up'),
      fieldNames: ['sum_of_request_length'],
      isSortByOriginalValue: true,
      displayFunction: (item) => {
        return Utils.amountSuffixFormatterBytes(item['sum_of_request_length'], this.$t);
      },
      isSortable: true,
      classes: 'width-90px',
    };

    const dataNamesMap = {
      host: {
        tabHeader: this.$t('dashboard.tables.tabs.applications'),
        keyColumnHeader: this.$t('dashboard.tables.columns.host'),
        filter: 'host',
      },
      country: {
        tabHeader: this.$t('dashboard.tables.tabs.countries'),
        keyColumnHeader: this.$t('dashboard.tables.columns.country'),
        filter: 'country',
      },
      ip: {
        tabHeader: this.$t('dashboard.tables.tabs.sources'),
        keyColumnHeader: this.$t('dashboard.tables.columns.ipAddress'),
        filter: 'ip',
      },
      waap_id: {
        tabHeader: this.$t('dashboard.tables.tabs.sessions'),
        keyColumnHeader: this.$t('dashboard.tables.columns.waapId'),
        filter: 'waap_id',
      },
      url: {
        tabHeader: this.$t('dashboard.tables.tabs.targets'),
        keyColumnHeader: this.$t('dashboard.tables.columns.url'),
        filter: 'url',
      },
      reason: {
        tabHeader: this.$t('dashboard.tables.tabs.blockedAndMonitored'),
        keyColumnHeader: this.$t('dashboard.tables.columns.reason'),
        filter: 'reason',
      },
      referer: {
        tabHeader: this.$t('dashboard.tables.tabs.referers'),
        keyColumnHeader: this.$t('dashboard.tables.columns.referer'),
        filter: 'referer',
      },
      user_agent: {
        tabHeader: this.$t('dashboard.tables.tabs.browsers'),
        keyColumnHeader: this.$t('dashboard.tables.columns.userAgent'),
        filter: 'user_agent',
      },
      organization: {
        tabHeader: this.$t('dashboard.tables.tabs.organizations'),
        keyColumnHeader: this.$t('dashboard.tables.columns.organization'),
        filter: 'organization',
      },
      total_time: {
        tabHeader: this.$t('dashboard.tables.tabs.totalTime'),
        keyColumnHeader: this.$t('dashboard.tables.columns.url'),
        filter: 'url',
      },
      waap_time: {
        tabHeader: this.$t('dashboard.tables.tabs.waapTime'),
        keyColumnHeader: this.$t('dashboard.tables.columns.url'),
        filter: 'url',
      },
      origin_time: {
        tabHeader: this.$t('dashboard.tables.tabs.originTime'),
        keyColumnHeader: this.$t('dashboard.tables.columns.url'),
        filter: 'url',
      },
    } as DataNamesMap;

    return {
      dataNamesMap: dataNamesMap,
      baseColumn,
      baseKeyColumn,
      hitsColumn,
      passedColumn,
      blockedColumn,
      challengesColumn,
      humansColumn,
      botsColumn,
      blockageColumn,
      downColumn,
      upColumn,
      countries: {} as Countries,
      windowWidth: window.innerWidth,

      // Context Menu
      contextMenuItem: null as DashboardTableEntry,
      contextMenuItemPropertyKey: null as string,
      contextMenuTop: '0px',
      contextMenuLeft: '0px',
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useDatasetsStore),

    tabs(): DashboardTableTab[] {
      return [
        {
          title: this.dataNamesMap['host'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconApplications' : '',
          defaultSortColumnIndex: 1,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'host',
              title: this.dataNamesMap['host'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.blockageColumn,
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('host'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['country'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconCountries' : '',
          defaultSortColumnIndex: 2,
          tableColumns: [
            {
              id: 'country-flag',
              title: 'CountryFlag',
              hideTitle: true,
              fieldNames: ['key'],
              isSortByOriginalValue: true,
              displaySlotName: 'countryFlagCell',
              displayFunction: (item) => {
                return item.key || '';
              },
              tooltipFunction: (item) => {
                return item.key || '';
              },
              tooltipFunctionAlwaysDisplayed: true,
              isSortable: true,
              classes: 'width-40px',
            },
            {
              ...this.baseKeyColumn,
              id: 'country',
              title: this.dataNamesMap['country'].keyColumnHeader,
              displayFunction: (item) => {
                return item.key ? item.key : '-';
              },
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('country'),
          NumberOfResultsColumn: 1,
        },
        {
          title: this.dataNamesMap['ip'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconSources' : '',
          defaultSortColumnIndex: 4,
          tableColumns: [
            {
              id: 'country-flag',
              title: 'CountryFlag',
              hideTitle: true,
              fieldNames: ['first_geo_country'],
              isSortByOriginalValue: true,
              displaySlotName: 'countryFlagCell',
              displayFunction: (item) => {
                return item['first_geo_country'] || '';
              },
              tooltipFunction: (item) => {
                return item['first_geo_country'] || '';
              },
              tooltipFunctionAlwaysDisplayed: true,
              isSortable: true,
              classes: 'width-40px',
            },
            {
              ...this.baseKeyColumn,
              id: 'ip',
              title: this.dataNamesMap['ip'].keyColumnHeader,
              classes: 'width-170px',
            },
            {
              id: 'asn',
              title: this.$t('dashboard.tables.columns.asn'),
              fieldNames: ['first_asn'],
              displayFunction: (event) => {
                return this.displayFunctionEmptyToDash(event, 'first_asn');
              },
              isSortable: true,
              classes: 'width-100px',
              cellContentClasses: 'ellipsis',
            },
            {
              id: 'organization',
              title: this.$t('dashboard.tables.columns.organization'),
              fieldNames: ['first_organization'],
              displayFunction: (event) => {
                return this.displayFunctionEmptyToDash(event, 'first_organization');
              },
              isSortable: true,
              cellContentClasses: 'ellipsis',
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('ip'),
          NumberOfResultsColumn: 1,
        },
        {
          title: this.dataNamesMap['waap_id'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconSessions' : '',
          defaultSortColumnIndex: 1,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'waap_id',
              title: this.dataNamesMap['waap_id'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('waap_id'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['url'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconTargets' : '',
          defaultSortColumnIndex: 1,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'url',
              title: this.dataNamesMap['url'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('url'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['reason'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconSignatures' : '',
          defaultSortColumnIndex: 1,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'reason',
              title: this.dataNamesMap['reason'].keyColumnHeader,
            },
            {
              ...this.baseColumn,
              id: 'hits',
              title: this.$t('dashboard.tables.columns.numberOfRequests'),
              fieldNames: ['num_of_requests'],
              isSortable: true,
              classes: 'width-200px',
            },
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('reason'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['referer'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconReferers' : '',
          defaultSortColumnIndex: 1,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'referer',
              title: this.dataNamesMap['referer'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('referer'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['user_agent'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconBrowsers' : '',
          defaultSortColumnIndex: 1,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'user-agent',
              title: this.dataNamesMap['user_agent'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('user_agent'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['organization'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconOrganizations' : '',
          defaultSortColumnIndex: 2,
          tableColumns: [
            {
              id: 'asn',
              title: this.$t('dashboard.tables.columns.asn'),
              fieldNames: ['first_asn'],
              onContextMenu: (event: PointerEvent, item) => {
                this.openContextMenu(event, item, 'first_asn');
              },
              onClick: (event: PointerEvent, item) => {
                this.openContextMenu(event, item, 'first_asn');
              },
              displayFunction: (event) => {
                return this.displayFunctionEmptyToDash(event, 'first_asn');
              },
              isSortable: true,
              classes: 'width-100px',
              cellContentClasses: 'ellipsis is-clickable',
            },
            {
              ...this.baseKeyColumn,
              id: 'organization',
              title: this.dataNamesMap['organization'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.humansColumn,
            this.botsColumn,
            this.generateTimeColumn(
              'latency',
              this.$t('dashboard.tables.columns.latency'),
              'avg_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('organization'),
          NumberOfResultsColumn: 1,
        },
        {
          title: this.dataNamesMap['total_time'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconTimes' : '',
          defaultSortColumnIndex: 5,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'total-time',
              title: this.dataNamesMap['total_time'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.generateTimeColumn(
              'max-time',
              this.$t('dashboard.tables.columns.max'),
              'max_total_time'
            ),
            this.generateTimeColumn(
              'avg-time',
              this.$t('dashboard.tables.columns.avg'),
              'avg_total_time'
            ),
            this.generateTimeColumn(
              'min-time',
              this.$t('dashboard.tables.columns.min'),
              'min_total_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('total_time'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['waap_time'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconTimes' : '',
          defaultSortColumnIndex: 5,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'rbz-time',
              title: this.dataNamesMap['waap_time'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.generateTimeColumn(
              'max-time',
              this.$t('dashboard.tables.columns.max'),
              'max_waap_time'
            ),
            this.generateTimeColumn(
              'avg-time',
              this.$t('dashboard.tables.columns.avg'),
              'avg_waap_time'
            ),
            this.generateTimeColumn(
              'min-time',
              this.$t('dashboard.tables.columns.min'),
              'min_waap_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('waap_time'),
          NumberOfResultsColumn: 0,
        },
        {
          title: this.dataNamesMap['origin_time'].tabHeader,
          icon: this.windowWidth >= 1920 ? 'IconTimes' : '',
          defaultSortColumnIndex: 5,
          tableColumns: [
            {
              ...this.baseKeyColumn,
              id: 'origin-time',
              title: this.dataNamesMap['origin_time'].keyColumnHeader,
            },
            this.hitsColumn,
            this.passedColumn,
            this.blockedColumn,
            this.challengesColumn,
            this.generateTimeColumn(
              'max-time',
              this.$t('dashboard.tables.columns.max'),
              'max_origin_time'
            ),
            this.generateTimeColumn(
              'avg-time',
              this.$t('dashboard.tables.columns.avg'),
              'avg_origin_time'
            ),
            this.generateTimeColumn(
              'min-time',
              this.$t('dashboard.tables.columns.min'),
              'min_origin_time'
            ),
            this.downColumn,
            this.upColumn,
          ] as RbzTableColumnOptions[],
          dataFilter: this.generateDataFilter('origin_time'),
          NumberOfResultsColumn: 0,
        },
      ];
    },

    eventsLogPath(): string {
      return `/${this.selectedBranch}/events-log`;
    },
  },
  methods: {
    loadCountries() {
      this.datasetsStore.getCountries()?.then((data: Countries) => {
        this.countries = data;
      });
    },

    getCountryCode(countryName: string) {
      const countryCode = Object.keys(this.countries).find((key: keyof Countries) => {
        return this.countries[key].toLowerCase() === countryName?.toLowerCase();
      });
      return _.escape(countryCode?.toLowerCase());
    },

    generateDataFilter(label: string) {
      return (data: DashboardTableEntry[]) => {
        return data.filter((item: DashboardTableEntry) => {
          return item.label === label;
        });
      };
    },

    generateTimeColumn(id: string, title: string, fieldName: string): RbzTableColumnOptions {
      return {
        ...this.baseColumn,
        id: id,
        title: title,
        fieldNames: [fieldName],
        isSortByOriginalValue: true,
        displayFunction: (item) => {
          return `${(item[fieldName] || 0).toFixed(3)}s`;
        },
        isSortable: true,
      };
    },

    displayFunctionEmptyToDash(item: DashboardTableEntry, key: string) {
      if (typeof item[key] === 'boolean' || item[key]) {
        return item[key];
      } else {
        return '-';
      }
    },

    getBlockage(item: DashboardTableEntry) {
      return (
        ((item['num_of_challenges'] + item['num_of_blocked_requests']) / item['num_of_requests']) *
        100
      );
    },

    setContextMenu(top: number, left: number) {
      const largestHeight =
        window.innerHeight - this.$refs.contextMenuRef.contextMenu.offsetHeight - 25;
      const largestWidth =
        window.innerWidth - this.$refs.contextMenuRef.contextMenu.offsetWidth - 25;
      if (top > largestHeight) {
        top = largestHeight;
      }
      if (left > largestWidth) {
        left = largestWidth;
      }
      this.contextMenuTop = top + 'px';
      this.contextMenuLeft = left + 'px';
    },

    closeContextMenu() {
      this.$refs.contextMenuRef.closeMenu();
      this.contextMenuItem = null;
      document
        .getElementsByClassName('main-content')[0]
        ?.removeEventListener('scroll', this.closeContextMenu);
    },

    openContextMenu(event: PointerEvent, item: DashboardTableEntry, propertyKey: string) {
      this.$refs.contextMenuRef.openMenu();
      this.contextMenuItem = item;
      this.contextMenuItemPropertyKey = propertyKey;
      nextTick(() => {
        this.setContextMenu(event.y, event.x);
        setTimeout(() => {
          document
            .getElementsByClassName('main-content')[0]
            ?.addEventListener('scroll', this.closeContextMenu);
        }, 100);
      });
      event.preventDefault();
    },

    contextMenuFilter(isNegative?: boolean) {
      let operator: string;
      if (this.contextMenuItemPropertyKey === 'key') {
        operator = this.dataNamesMap[this.contextMenuItem.label].filter;
      } else {
        operator = this.contextMenuItemPropertyKey;
        operator = operator.replace('first_', '');
      }
      let operand: string | number | boolean =
        this.contextMenuItem[this.contextMenuItemPropertyKey];
      if (typeof operand === 'string') {
        if (operand) {
          operand = `"${operand.replace(/"/g, '\\"')}"`;
        } else {
          operand = `""`;
        }
      }
      if (typeof operand === 'boolean') {
        return `${operator}=${isNegative ? !operand : operand}`;
      } else {
        return `${operator}${isNegative ? '!' : ''}=${operand}`;
      }
    },

    contextMenuCopyToClipboard() {
      navigator.clipboard.writeText(this.contextMenuItem[this.contextMenuItemPropertyKey]);
      this.closeContextMenu();
    },

    contextMenuAddToFilter(isNegative?: boolean) {
      this.$emit('add-filter', this.contextMenuFilter(isNegative));
      this.closeContextMenu();
    },

    navigateToEventsLog(filter?: string) {
      const query = this.getQuery(filter);
      this.$router.push({
        path: this.eventsLogPath,
        query: query,
      });
      this.closeContextMenu();
    },

    getQuery(filter?: string): RouteParams {
      let query = this.$route.query?.filters?.toString();
      if (filter) {
        query += query ? ', ' : '';
        query += filter;
      }
      return { filters: query };
    },
  },
  mounted() {
    this.loadCountries();
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.dashboard-panel-content {
  margin: 12px;
}

:deep(.tabs-wrapper > .tabs-header) {
  margin-bottom: 8px;
}

:deep(.rbz-table) {
  .high-blockage {
    background-color: rgba(map.get(colors.$colors, 'bright-red'), 0.1);
  }

  .border-left {
    border-left: 1px solid map.get(colors.$colors, 'silver');
  }

  .border-right {
    border-right: 1px solid map.get(colors.$colors, 'silver');
  }

  .flag-cell-wrapper {
    align-items: center;
    display: flex;
    justify-content: center;
    overflow: hidden;

    .flag {
      margin: -0.9em -1.2em;
      transform: scale(0.25);
    }

    .flag-placeholder {
      margin: -0.9em -1.2em;
      transform: scale(0.25);
    }
  }

  .passed {
    color: map.get(colors.$colors, 'fruit-salad');
  }

  .blocked {
    color: map.get(colors.$colors, 'bright-red');
  }

  .challenged {
    color: map.get(colors.$colors, 'whisky');
  }

  .humans {
    color: map.get(colors.$colors, 'jordy-blue');
  }

  .bots {
    color: map.get(colors.$colors, 'jaffa');
  }
}

.table-loader {
  position: relative !important;
}
</style>
